import { Currency } from '../types';

interface ExchangeRates {
  [key: string]: number;
}

let cachedRates: ExchangeRates | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 1000 * 60 * 60; // 1 hour

/**
 * Fetches exchange rates from Frankfurter API (ECB data, free, no key).
 * Returns rates relative to NOK (i.e., how many NOK per 1 unit of each currency).
 */
export async function getExchangeRates(): Promise<ExchangeRates> {
  const now = Date.now();
  if (cachedRates && now - cacheTimestamp < CACHE_DURATION) {
    return cachedRates;
  }

  try {
    const res = await fetch('https://api.frankfurter.dev/v1/latest?base=NOK');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();

    // data.rates gives us: { USD: 0.089, EUR: 0.082, ... } (how many X per 1 NOK)
    // We want the inverse: how many NOK per 1 X
    const rates: ExchangeRates = { NOK: 1 };
    for (const [currency, rate] of Object.entries(data.rates)) {
      rates[currency] = 1 / (rate as number);
    }

    cachedRates = rates;
    cacheTimestamp = now;
    return rates;
  } catch (error) {
    console.error('Failed to fetch exchange rates:', error);
    // Return fallback rates if fetch fails
    return cachedRates || {
      NOK: 1,
      USD: 11.0,
      EUR: 11.5,
      GBP: 13.5,
      SEK: 1.05,
      DKK: 1.55,
    };
  }
}

/**
 * Convert an amount from one currency to NOK.
 */
export function convertToNOK(amount: number, currency: Currency, rates: ExchangeRates): number {
  if (currency === 'NOK') return amount;
  const rate = rates[currency];
  if (!rate) return amount; // fallback: return as-is
  return amount * rate;
}

/**
 * Format an amount with currency symbol.
 */
export function formatCurrency(amount: number, currency: Currency): string {
  const formatted = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 }).format(Math.round(amount));
  const symbols: Record<Currency, string> = {
    NOK: 'kr',
    USD: '$',
    EUR: '€',
    GBP: '£',
    SEK: 'kr',
    DKK: 'kr',
  };
  const symbol = symbols[currency] || currency;

  if (['USD', 'EUR', 'GBP'].includes(currency)) {
    return `${symbol}${formatted}`;
  }
  return `${formatted} ${symbol}`;
}
